import type { DateTime } from "luxon";
import { useMemo } from "react";
import { useCensusGrouping } from "../components/StaffTable/useCensusGrouping";
import { FEATURE_FLAGS } from "../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useUserSettingsContext } from "../contexts/Staffing/UserSettingsContext";
import { useFeatureFlag } from "../hooks/useOpenFeatureFlag";
import { computeHppdBreakdown, type HppdBreakdownEntry } from "../utils/hppdBreakdown";
import { HppdSeparationType, type Nurse, type NurseInShift } from "../utils/models";
import { filterNursesForStats } from "../utils/statsCalculations";
import { buildNurseUnionMap, getShiftWorkHours } from "../utils/utilFunctions";

export interface GroupHppdBreakdown {
	locationName: string;
	breakdown: HppdBreakdownEntry[];
	totalHppd: number;
}

export interface UseHppdByDimensionResult {
	totalBreakdown: HppdBreakdownEntry[];
	totalHppd: number;
	groupedBreakdown: GroupHppdBreakdown[];
	isSeparated: boolean;
}

/**
 * Custom hook to calculate HPPD breakdown by a specified dimension (role, role groups, or jobs) based on user settings.
 * It returns both the total breakdown and a grouped breakdown by location (if separation is enabled).
 */
export function useHppdByDimension(nurses: NurseInShift[], census: number, date: DateTime): UseHppdByDimensionResult {
	const { chosenDepartment, hospitalData, getNurseRoleEnum } = useHospitalData();
	const { settings } = useUserSettingsContext();
	const { groupNursesByLocation } = useCensusGrouping(date);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);

	return useMemo(() => {
		const separationType = settings.hppdSeparationType;
		const { breakRulesConfig = [], department_id } = chosenDepartment;
		// FF OFF: pass allNurses so filterNursesForStats can read the per-nurse flag.
		const allNurses: Nurse[] = jobManagementEnabled ? [] : hospitalData.nurses;
		// Full roster, never `allNurses` — see `useCensusGrouping` for why that would drop the gate.
		const unionByNurseId = buildNurseUnionMap(hospitalData.nurses);

		if (separationType === HppdSeparationType.None) {
			// Return single aggregate HPPD so the UI always reads from this hook
			const totalHours = nurses
				.filter((n) => filterNursesForStats(n, jobManagementEnabled, allNurses, false, false))
				.reduce((sum, nurse) => {
					const role = getNurseRoleEnum(nurse.nurseId);
					return (
						sum +
						(!role
							? 0
							: getShiftWorkHours(
									department_id,
									nurse.endTime,
									nurse.startTime,
									breakRulesConfig,
									role,
									nurse.type,
									nurse.jobId ? [nurse.jobId] : [],
									jobManagementEnabled,
									unionByNurseId.get(nurse.nurseId) ?? "",
								))
					);
				}, 0);
			const hppd = census > 0 ? Number((totalHours / census).toFixed(2)) : 0;

			return {
				totalBreakdown: [],
				totalHppd: hppd,
				groupedBreakdown: [],
				isSeparated: false,
			};
		}

		// Compute total breakdown
		const totalBreakdown = computeHppdBreakdown(
			nurses,
			census,
			department_id,
			breakRulesConfig,
			separationType,
			chosenDepartment,
			jobManagementEnabled,
			allNurses,
			unionByNurseId,
		);

		// Compute per-location-group breakdown
		const groupedNurses = groupNursesByLocation(nurses);
		const groupedBreakdown: GroupHppdBreakdown[] = groupedNurses.map((group) => ({
			locationName: group.locationName,
			breakdown: computeHppdBreakdown(
				group.nurses,
				group.censusCount,
				department_id,
				breakRulesConfig,
				separationType,
				chosenDepartment,
				jobManagementEnabled,
				allNurses,
				unionByNurseId,
			),
			totalHppd: group.hppd,
		}));

		return {
			totalBreakdown,
			totalHppd: totalBreakdown.reduce((sum, entry) => sum + entry.hppd, 0),
			groupedBreakdown,
			isSeparated: true,
		};
	}, [nurses, census, chosenDepartment, hospitalData.nurses, jobManagementEnabled, settings.hppdSeparationType, groupNursesByLocation, getNurseRoleEnum]);
}
